import React, { useState, useEffect } from 'react';
import * as FiIcons from 'react-icons/fi';
import ReactECharts from 'echarts-for-react';
import SafeIcon from '../../common/SafeIcon';

const { FiBrain, FiBarChart2, FiCheck } = FiIcons;

const CognitiveLevelAnalysis = ({ questions }) => {
  const [distribution, setDistribution] = useState({
    'Remember': 0,
    'Understand': 0,
    'Apply': 0,
    'Analyze': 0,
    'Evaluate': 0,
    'Create': 0
  });

  const [keywordMatches, setKeywordMatches] = useState([]);

  useEffect(() => {
    if (!questions.length) return;
    
    // Initialize distribution counts
    const newDistribution = {
      'Remember': 0,
      'Understand': 0,
      'Apply': 0,
      'Analyze': 0,
      'Evaluate': 0,
      'Create': 0
    };
    
    const newKeywordMatches = [];
    
    // Process each question
    questions.forEach(question => {
      const level = determineCognitiveLevel(question.text);
      newDistribution[level] = (newDistribution[level] || 0) + 1;
      
      // Find and store keyword matches for explanation
      const matches = findKeywordMatches(question.text);
      if (matches.length > 0) {
        newKeywordMatches.push({
          id: question.id,
          text: question.text.substring(0, 80) + (question.text.length > 80 ? '...' : ''),
          level,
          keywords: matches
        });
      }
    });
    
    setDistribution(newDistribution);
    setKeywordMatches(newKeywordMatches.slice(0, 10)); // Limit to 10 examples
    
  }, [questions]);
  
  // Define cognitive level keywords
  const cognitiveKeywords = {
    'Remember': [
      'define', 'list', 'name', 'identify', 'recall', 'recognize', 'state', 'select', 
      'label', 'outline', 'match', 'what is', 'when did', 'who was', 'where is'
    ],
    'Understand': [
      'explain', 'describe', 'interpret', 'summarize', 'infer', 'paraphrase', 
      'classify', 'compare', 'discuss', 'distinguish', 'predict', 'associate',
      'why did', 'how would you describe'
    ],
    'Apply': [
      'apply', 'solve', 'illustrate', 'calculate', 'use', 'demonstrate', 'implement',
      'modify', 'discover', 'prepare', 'produce', 'show', 'how would you use'
    ],
    'Analyze': [
      'analyze', 'differentiate', 'organize', 'deduce', 'distinguish', 'examine', 
      'contrast', 'investigate', 'categorize', 'separate', 'what evidence can you find',
      'what is the relationship between', 'why do you think'
    ],
    'Evaluate': [
      'evaluate', 'assess', 'critique', 'judge', 'justify', 'defend', 'determine',
      'prioritize', 'rate', 'validate', 'recommend', 'convince', 'what is your opinion',
      'do you agree', 'would it be better if'
    ],
    'Create': [
      'create', 'design', 'develop', 'compose', 'construct', 'formulate', 'invent',
      'devise', 'plan', 'imagine', 'propose', 'generate', 'how would you design',
      'what would happen if', 'can you propose an alternative'
    ]
  };
  
  // Function to determine cognitive level based on question text
  const determineCognitiveLevel = (text) => {
    const lowerText = text.toLowerCase();
    
    // Check each level, starting from highest (most complex)
    for (const level of ['Create', 'Evaluate', 'Analyze', 'Apply', 'Understand', 'Remember']) {
      const keywords = cognitiveKeywords[level];
      for (const keyword of keywords) {
        // Use word boundary to match whole words only
        const regex = new RegExp(`\\b${keyword}\\b`, 'i');
        if (regex.test(lowerText)) {
          return level;
        }
      }
    }
    
    // Default to "Understand" if no matches
    return 'Understand';
  };
  
  // Function to find matching keywords in text
  const findKeywordMatches = (text) => {
    const lowerText = text.toLowerCase();
    const matches = [];
    
    Object.entries(cognitiveKeywords).forEach(([level, keywords]) => {
      keywords.forEach(keyword => {
        const regex = new RegExp(`\\b${keyword}\\b`, 'i');
        if (regex.test(lowerText)) {
          matches.push({ keyword, level });
        }
      });
    });
    
    return matches;
  };
  
  // Get appropriate color for cognitive level
  const getLevelColor = (level) => {
    const colorMap = {
      'Remember': 'bg-indigo-100 text-indigo-800',
      'Understand': 'bg-blue-100 text-blue-800',
      'Apply': 'bg-green-100 text-green-800',
      'Analyze': 'bg-yellow-100 text-yellow-800',
      'Evaluate': 'bg-orange-100 text-orange-800',
      'Create': 'bg-red-100 text-red-800'
    };
    return colorMap[level] || 'bg-gray-100 text-gray-800';
  };
  
  // Prepare chart options for ECharts
  const getBarChartOptions = () => {
    const levels = Object.keys(distribution);
    const values = Object.values(distribution);
    
    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow'
        }
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: levels,
        axisTick: {
          alignWithLabel: true
        }
      },
      yAxis: {
        type: 'value'
      },
      series: [
        {
          name: 'Count',
          type: 'bar',
          barWidth: '60%',
          data: values,
          itemStyle: {
            color: function(params) {
              const colors = ['#818cf8', '#3b82f6', '#10b981', '#fbbf24', '#f97316', '#ef4444'];
              return colors[params.dataIndex];
            }
          }
        }
      ]
    };
  };

  // Prepare pyramid chart for Bloom's Taxonomy
  const getPyramidChartOptions = () => {
    const levels = Object.keys(distribution).reverse(); // Reverse to show pyramid properly
    const values = Object.keys(distribution).reverse().map(key => distribution[key]);
    
    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow'
        },
        formatter: function(params) {
          const dataIndex = params[0].dataIndex;
          return `${levels[dataIndex]}: ${values[dataIndex]}`;
        }
      },
      grid: {
        top: '5%',
        right: '10%',
        bottom: '5%',
        left: '10%'
      },
      xAxis: {
        type: 'value',
        position: 'top',
        splitLine: {
          lineStyle: {
            type: 'dashed'
          }
        }
      },
      yAxis: {
        type: 'category',
        data: levels,
        axisLine: { show: false },
        axisTick: { show: false },
        splitLine: { show: false }
      },
      series: [
        {
          name: 'Count',
          type: 'bar',
          stack: 'Total',
          label: {
            show: true,
            position: 'inside'
          },
          data: values,
          itemStyle: {
            color: function(params) {
              // Colors from bottom (Create) to top (Remember)
              const colors = ['#ef4444', '#f97316', '#fbbf24', '#10b981', '#3b82f6', '#818cf8'];
              return colors[params.dataIndex];
            }
          }
        }
      ]
    };
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
      <div className="flex items-center space-x-2 mb-6">
        <SafeIcon icon={FiBrain} className="text-primary-600" />
        <h3 className="text-lg font-semibold text-gray-800">Cognitive Level Analysis</h3>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div>
          <h4 className="font-medium text-gray-800 mb-4">Distribution by Bloom's Taxonomy Level</h4>
          <div className="h-64">
            <ReactECharts 
              option={getBarChartOptions()} 
              style={{ height: '100%', width: '100%' }}
            />
          </div>
        </div>
        
        <div>
          <h4 className="font-medium text-gray-800 mb-4">Bloom's Taxonomy Pyramid</h4>
          <div className="h-64">
            <ReactECharts 
              option={getPyramidChartOptions()} 
              style={{ height: '100%', width: '100%' }}
            />
          </div>
        </div>
      </div>
      
      <div className="mt-8">
        <h4 className="font-medium text-gray-800 mb-4">Cognitive Level Indicators</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {Object.entries(cognitiveKeywords).map(([level, keywords]) => (
            <div key={level} className="bg-gray-50 p-4 rounded-lg">
              <div className={`inline-block px-3 py-1 rounded-lg mb-2 ${getLevelColor(level)}`}>
                {level}
              </div>
              <div className="text-sm text-gray-600">
                {keywords.slice(0, 8).map((keyword, index) => (
                  <span key={index} className="inline-block bg-white px-2 py-1 rounded mr-2 mb-2 text-xs border border-gray-100">
                    {keyword}
                  </span>
                ))}
                {keywords.length > 8 && (
                  <span className="inline-block bg-white px-2 py-1 rounded mr-2 mb-2 text-xs border border-gray-100">
                    +{keywords.length - 8} more
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {keywordMatches.length > 0 && (
        <div className="mt-8">
          <h4 className="font-medium text-gray-800 mb-4">Sample Classifications</h4>
          <div className="space-y-4">
            {keywordMatches.slice(0, 5).map((match, index) => (
              <div key={index} className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-start">
                  <div className={`inline-block px-3 py-1 rounded-lg mr-3 ${getLevelColor(match.level)}`}>
                    {match.level}
                  </div>
                  <div className="flex-grow">
                    <p className="text-sm text-gray-700">{match.text}</p>
                    <div className="mt-2 flex flex-wrap">
                      {match.keywords.map((kw, i) => (
                        <div key={i} className="flex items-center mr-4 mb-1">
                          <SafeIcon icon={FiCheck} className="text-green-500 mr-1" />
                          <span className="text-xs text-gray-600">"{kw.keyword}" ({kw.level})</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      
      <div className="mt-8 bg-blue-50 p-4 rounded-lg">
        <h4 className="font-medium text-gray-800 mb-2">Assessment Balance Recommendation</h4>
        <div className="flex items-center text-sm text-gray-600 mb-3">
          <SafeIcon icon={FiBarChart2} className="mr-2 text-blue-500" />
          <p>For comprehensive assessment, aim for this distribution across cognitive levels:</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
          <div className="bg-white p-3 rounded border border-gray-100">
            <div className="font-medium mb-1">Lower Order (Remember, Understand)</div>
            <div className="text-gray-600">30-40% of questions</div>
          </div>
          <div className="bg-white p-3 rounded border border-gray-100">
            <div className="font-medium mb-1">Middle Order (Apply, Analyze)</div>
            <div className="text-gray-600">40-50% of questions</div>
          </div>
          <div className="bg-white p-3 rounded border border-gray-100">
            <div className="font-medium mb-1">Higher Order (Evaluate, Create)</div>
            <div className="text-gray-600">10-20% of questions</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CognitiveLevelAnalysis;